import React, { FC, useEffect, useState } from 'react';
import {
	BidListAuditResult,
	getPublishPreview,
	PublishProjectResult,
	publishProject,
} from './publishApi';
import { PublishPreview } from './types';
import { DEFAULT_BIDDER_AI_DISCLAIMER } from './publicApi';
import BidListAuditModal from './BidListAuditModal';
import './BidInvites.css';

interface PublishProjectModalProps {
	initialBidListAuditRun?: BidListAuditResult | null;
	projectId: number;
	projectName: string;
	onBidListAuditRunChange?: (run: BidListAuditResult) => void;
	onBidListAuditCompleted?: () => Promise<void> | void;
	onClose: () => void;
	onPublish?: (options: {
		aiDisclaimer: string;
		includeGroupingBulkAnswers: boolean;
		includeQuoteReview: boolean;
		includeScopeLocations: boolean;
	}) => Promise<PublishProjectResult | void>;
	onPublished?: (result: PublishProjectResult) => void;
}

const optionCardStyle = (checked: boolean): React.CSSProperties => ({
	alignItems: 'flex-start',
	background: checked ? '#F0F7FC' : '#FFFFFF',
	border: `1px solid ${checked ? '#5BA4D2' : '#DCE5EB'}`,
	borderRadius: 10,
	cursor: 'pointer',
	display: 'flex',
	gap: 12,
	padding: 14,
});

const PublishProjectModal: FC<PublishProjectModalProps> = ({
	initialBidListAuditRun = null,
	projectId,
	projectName,
	onBidListAuditRunChange,
	onBidListAuditCompleted,
	onClose,
	onPublish,
	onPublished,
}) => {
	const [preview, setPreview] = useState<PublishPreview | null>(null);
	const [loading, setLoading] = useState(true);
	const [publishing, setPublishing] = useState(false);
	const [includeGroupingBulkAnswers, setIncludeGroupingBulkAnswers] = useState(true);
	const [includeScopeLocations, setIncludeScopeLocations] = useState(true);
	const [includeQuoteReview, setIncludeQuoteReview] = useState(true);
	const [aiDisclaimer, setAiDisclaimer] = useState(DEFAULT_BIDDER_AI_DISCLAIMER);
	const [error, setError] = useState('');
	const [auditGateOpen, setAuditGateOpen] = useState(true);

	useEffect(() => {
		getPublishPreview(projectId)
			.then((nextPreview) => {
				setPreview(nextPreview);
				const savedOptions = nextPreview.publishOptions;
				if (savedOptions) {
					setIncludeGroupingBulkAnswers(savedOptions.includeGroupingBulkAnswers !== false);
					setIncludeScopeLocations(savedOptions.includeScopeLocations);
					setIncludeQuoteReview(savedOptions.includeQuoteReview);
					setAiDisclaimer(savedOptions.aiDisclaimer || DEFAULT_BIDDER_AI_DISCLAIMER);
				}
			})
			.catch((requestError) =>
				setError(requestError?.data?.message || 'Unable to prepare this publish.'),
			)
			.finally(() => setLoading(false));
	}, [projectId]);

	const publish = async () => {
		setPublishing(true);
		setError('');
		try {
			const options = {
				aiDisclaimer,
				includeGroupingBulkAnswers,
				includeQuoteReview,
				includeScopeLocations,
			};
			const result = onPublish
				? await onPublish(options)
				: await publishProject(projectId, options);
			if (result && onPublished) onPublished(result);
			onClose();
		} catch (requestError) {
			setError(requestError?.data?.message || 'Unable to publish invitations.');
		} finally {
			setPublishing(false);
		}
	};

	const published = Boolean(preview?.published);
	const republishRequired = Boolean(published && preview?.republishRequired);
	const alreadyPublished = published && !republishRequired;
	const modalTitle = republishRequired
		? 'Publish Updated Drawings to Bidders'
		: 'Publish Project to Bidders';
	let publishButtonLabel = 'Publish Invitations';
	if (republishRequired) publishButtonLabel = 'Publish Updated Drawings';
	if (publishing) publishButtonLabel = 'Publishing...';

	const continueFromAudit = async () => {
		await onBidListAuditCompleted?.();
		const nextPreview = await getPublishPreview(projectId);
		setPreview(nextPreview);
		setError('');
		setAuditGateOpen(false);
	};

	if (!loading && preview?.ready && !published && auditGateOpen && !preview.bidListAgentApplied) {
		return (
			<BidListAuditModal
				initialRun={initialBidListAuditRun}
				onCancel={onClose}
				onRunChange={onBidListAuditRunChange}
				onSend={continueFromAudit}
				onSkip={() => {
					setError('');
					setAuditGateOpen(false);
				}}
				projectId={projectId}
			/>
		);
	}

	return (
		<div
			className="bid-invites-modal-overlay"
			role="dialog"
			aria-modal="true"
			aria-label={modalTitle}
		>
			<div className="bid-invites-dialog">
				<header className="bid-invites-dialog-header">
					<div>
						<h2 style={{ margin: 0 }}>{modalTitle}</h2>
						<p style={{ color: '#667887', margin: '4px 0 0' }}>
							{republishRequired
								? `Review who will receive the latest drawings for ${projectName}.`
								: `Review who will receive an invitation for ${projectName}.`}
						</p>
					</div>
				</header>

				<div className="bid-invites-dialog-body">
					{loading && <p>Checking package readiness...</p>}
					{error && (
						<div role="alert" style={{ color: '#B42318', marginBottom: 12 }}>
							{error}
						</div>
					)}
					{preview && (
						<>
							<div
								style={{
									background: alreadyPublished || preview.ready ? '#EDF8F0' : '#FFF4E8',
									borderRadius: 8,
									marginBottom: 16,
									padding: 13,
								}}
							>
								<strong>
									{alreadyPublished && 'Project is Published'}
									{republishRequired && 'Updated drawings are ready to publish'}
									{!published && preview.ready && 'Ready to publish'}
									{!published && !preview.ready && 'Action needed before publishing'}
								</strong>
								{preview.blockers?.map((blocker) => (
									<div key={blocker} style={{ marginTop: 5 }}>
										{blocker}
									</div>
								))}
							</div>

							{(!published || republishRequired) && (
								<section aria-labelledby="bidder-access-options" style={{ marginBottom: 18 }}>
									<div style={{ marginBottom: 10 }}>
										<strong id="bidder-access-options">Bidder workspace access</strong>
										<p style={{ color: '#667887', fontSize: 13, margin: '3px 0 0' }}>
											Choose which review tools invited bidders can use. All options are included by
											default.
										</p>
									</div>
									<div style={{ display: 'grid', gap: 10 }}>
										<label style={optionCardStyle(includeScopeLocations)}>
											<input
												checked={includeScopeLocations}
												onChange={(event) => setIncludeScopeLocations(event.target.checked)}
												type="checkbox"
											/>
											<span>
												<strong style={{ display: 'block' }}>Include scope locations</strong>
												<small style={{ color: '#667887' }}>
													Let bidders open the drawing and specification references behind scope
													checklist items.
												</small>
											</span>
										</label>
										<label style={optionCardStyle(includeQuoteReview)}>
											<input
												checked={includeQuoteReview}
												onChange={(event) => setIncludeQuoteReview(event.target.checked)}
												type="checkbox"
											/>
											<span>
												<strong style={{ display: 'block' }}>Include quote review</strong>
												<small style={{ color: '#667887' }}>
													Let bidders upload a quote and review an automated comparison before
													submitting.
												</small>
											</span>
										</label>
										<label style={optionCardStyle(includeGroupingBulkAnswers)}>
											<input
												checked={includeGroupingBulkAnswers}
												onChange={(event) => setIncludeGroupingBulkAnswers(event.target.checked)}
												type="checkbox"
											/>
											<span>
												<strong style={{ display: 'block' }}>Allow grouping-level Yes/No</strong>
												<small style={{ color: '#667887' }}>
													Let bidders answer Yes or No for an entire scope grouping at once, then
													adjust individual line items.
												</small>
											</span>
										</label>
									</div>
									<div
										style={{
											background: '#F8FBFD',
											border: '1px solid #DCE7EE',
											borderRadius: 10,
											marginTop: 14,
											padding: 14,
										}}
									>
										<label
											htmlFor="bidder-ai-disclaimer"
											style={{ color: '#17384F', display: 'block', fontWeight: 700 }}
										>
											AI disclaimer
										</label>
										<p style={{ color: '#667887', fontSize: 13, margin: '4px 0 10px' }}>
											This message appears above the bidder's interactive scope-location reviewer.
										</p>
										<textarea
											id="bidder-ai-disclaimer"
											maxLength={2000}
											onChange={(event) => setAiDisclaimer(event.target.value)}
											rows={4}
											style={{
												border: '1px solid #B9C8D4',
												borderRadius: 8,
												boxSizing: 'border-box',
												font: 'inherit',
												lineHeight: 1.5,
												padding: 11,
												resize: 'vertical',
												width: '100%',
											}}
											value={aiDisclaimer}
										/>
										<small style={{ color: '#718896' }}>
											{aiDisclaimer.length.toLocaleString()} / 2,000 characters
										</small>
									</div>
								</section>
							)}

							{preview.packages.map((pkg) => (
								<div
									key={pkg.bidPackageId}
									style={{
										border: '1px solid #DCE5EB',
										borderRadius: 7,
										marginBottom: 9,
										padding: 13,
									}}
								>
									<div style={{ display: 'flex', justifyContent: 'space-between' }}>
										<strong>{pkg.name}</strong>
										<span>
											{pkg.companyCount} companies / {pkg.validRecipientCount} valid recipients
										</span>
									</div>
									{pkg.invalidCompanies?.length > 0 && (
										<div style={{ color: '#A15C00', fontSize: 12, marginTop: 7 }}>
											Excluded (no valid email):{' '}
											{pkg.invalidCompanies.map((company) => company.name).join(', ')}
										</div>
									)}
								</div>
							))}
							<p style={{ color: '#667887', fontSize: 13 }}>
								Each company receives a private invitation; contacts are blind-copied so recipients
								cannot see one another.
							</p>
						</>
					)}
				</div>

				<footer className="bid-invites-dialog-footer">
					<button type="button" className="bid-invites-button" onClick={onClose}>
						{alreadyPublished ? 'Close' : 'Cancel'}
					</button>
					{!alreadyPublished && (
						<button
							type="button"
							className="bid-invites-button primary"
							disabled={!preview?.ready || publishing}
							onClick={publish}
						>
							{publishButtonLabel}
						</button>
					)}
				</footer>
			</div>
		</div>
	);
};

export default PublishProjectModal;
